import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { IRegistryInvoice } from "dto/Invoices/IRegistryInvoice";
import { IStatus } from "dto/IStatus";
import StatusBadge from "components/StatusBadge";
import acceptanceService, { ApprovalAction } from "api/acceptanceService";
import registryInvoicesService from "api/registryInvoicesService";
import { resolveInvoiceContractor } from "components/Registry/KSeFInvoiceRegister/invoiceContractor";
import NipWithCopy from "components/Registry/KSeFInvoiceRegister/Cells/NipWithCopy";
import { cleanCompanyName } from "utils/cleanCompanyName";
import { formatMoney } from "utils/formatMoney";
import styles from "./styles.module.scss";

// Treść metryki dokumentu (Dane dokumentu + Procedura akceptacji) — bez nagłówka
// i bez logiki podglądu pliku. Wyodrębnione, by dało się użyć jako panel prawej
// strony w InvoicePdfSlide (metryka obok podglądu dokumentu w Rozrachunkach) oraz
// wewnątrz samego InvoiceMetricSlide (lista „Faktury i rachunki").

const fmtDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const fmtDateTime = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString("pl-PL", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "";

const DOC_TYPE_LABELS: Record<string, string> = {
  Invoice: "Faktura",
  Receipt: "Rachunek",
};

const STATUS_LABELS: Record<string, string> = {
  AwaitingPayment: "Oczekuje na płatność",
  Paid: "Opłacona",
  Overdue: "Po terminie",
  Cancelled: "Anulowana",
};

// Rzeczywiste zdarzenia akceptacji (nie szablon) — etykieta i kolor kropki na osi.
const ACTION_LABEL: Record<ApprovalAction, string> = {
  submit: "Przekazano do akceptacji",
  approve: "Zaakceptowano",
  reject: "Odrzucono",
  send_back: "Zwrócono do poprawy",
};

const ACTION_COLOR: Record<ApprovalAction, string> = {
  submit: "#5d49c7",
  approve: "#2e7d32",
  reject: "#c62828",
  send_back: "#e65100",
};

const InvoiceMetricBody = ({
  invoice,
  workflowStatus,
}: {
  invoice: IRegistryInvoice;
  // Status workflow z listy (edytowalny badge z katalogu statusów). Przekazywany
  // z rejestru „Faktury i rachunki", by metryka pokazywała TEN SAM status co kolumna
  // na liście. Nieprzekazany (np. w Rozrachunkach) — pole „Status" pomijamy.
  workflowStatus?: IStatus;
}) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  const isKsef = invoice.SourceType === "K";

  // Rzeczywista historia akceptacji TEJ faktury (nie szablon procedury). Sekcję
  // „Procedura akceptacji" pokazujemy TYLKO, gdy faktura naprawdę przez nią przeszła.
  // Faktury KSeF (nie-rejestrowe) nie mają wpisu w /registry/invoices — pomijamy odpyt.
  const { data: timeline = [] } = acceptanceService.useGetApprovalTimeline(
    "invoice",
    isKsef ? undefined : invoice.Id,
  );

  // (magistrat: bez sekcji Dekretacja ksiegowa i Pozycje - dekretacja FK i linie
  // faktury nie sa przeniesione z galezi claude.)

  const docTypeLabel = DOC_TYPE_LABELS[invoice.DocumentType] ?? "Dokument";
  const statusLabel = invoice.Status
    ? STATUS_LABELS[invoice.Status] ?? "—"
    : "—";
  const currency = invoice.Currency ?? "PLN";

  // Kontrahent — jak w kolumnie „Kontrahent" na liście (Seller/Buyer wg charakteru
  // dokumentu). Fallback do pól rejestrowych (IssuerCompany/…), gdy obiekt nie niesie
  // węzła Seller (np. metryka w Rozrachunkach).
  const party = resolveInvoiceContractor(invoice);
  const contractorName =
    cleanCompanyName(party.name) ||
    invoice.IssuerCompany ||
    invoice.IssuerFullName ||
    invoice.IssuerNIP ||
    "—";
  const contractorNip = party.nip || invoice.IssuerNIP;

  // Przedmiot faktury KSeF nie jest w payloadzie listy (jest tylko w XML) — dociągamy
  // go z prefillu księgowania (pozycje P_7) na żądanie, przy otwarciu metryki.
  const [ksefSubject, setKsefSubject] = useState<string | null>(null);
  useEffect(() => {
    if (!isKsef || !invoice.KsefNumber || invoice.Subject) return;
    let cancelled = false;
    registryInvoicesService
      .getKsefAccountingPrefill(invoice.KsefNumber)
      .then((p) => {
        const items = p?.ItemNames ?? [];
        if (!cancelled && items.length) {
          setKsefSubject(items.length > 1 ? `${items[0]} i inne` : items[0]);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isKsef, invoice.KsefNumber, invoice.Subject]);
  const subjectValue = invoice.Subject || ksefSubject || "—";

  return (
    <div className={styles.body}>
      {/* Dane dokumentu — sekcja zwijana */}
      <div
        className={styles.sectionTitle}
        onClick={() => toggleSection("info")}
      >
        <span>Dane dokumentu</span>
        <FontAwesomeIcon
          className={styles.sectionChevron}
          icon={collapsed.info ? faChevronRight : faChevronDown}
        />
      </div>
      {!collapsed.info && (
        <div className={styles.metaCard}>
          <div className={styles.metaTitleRow}>
            <div className={styles.metaTitleLabel}>Kontrahent</div>
            <div className={styles.metaTitleValue}>
              {contractorName}
              {contractorNip && (
                <div className={styles.metaTitleSub}>
                  <NipWithCopy nip={contractorNip} />
                </div>
              )}
            </div>
          </div>
          <div className={styles.metaTitleRow}>
            <div className={styles.metaTitleLabel}>Przedmiot</div>
            <div className={styles.metaTitleValue}>{subjectValue}</div>
          </div>
          <div className={styles.metaGrid}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Numer</span>
              <span className={styles.metaValue}>
                {invoice.InvoiceNumber || "—"}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Typ</span>
              <span className={styles.metaValue}>{docTypeLabel}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Wystawiono</span>
              <span className={styles.metaValue}>
                {fmtDate(invoice.IssueDate)}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Termin płatności</span>
              <span className={styles.metaValue}>
                {fmtDate(invoice.PaymentDate)}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Netto</span>
              <span className={styles.metaValue}>
                {formatMoney(invoice.NetAmount ?? 0)} {currency}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>VAT</span>
              <span className={styles.metaValue}>
                {formatMoney(invoice.VatAmount ?? 0)} {currency}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Brutto</span>
              <span className={styles.metaValue}>
                {formatMoney(invoice.GrossAmount ?? 0)} {currency}
              </span>
            </div>
            {workflowStatus && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Status</span>
                <span className={styles.metaValue}>
                  <StatusBadge status={workflowStatus} showName small />
                </span>
              </div>
            )}
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Status płatności</span>
              <span className={styles.metaValue}>{statusLabel}</span>
            </div>
          </div>
        </div>
      )}

      {/* Procedura akceptacji — TYLKO gdy faktura rzeczywiście przez nią przeszła */}
      {timeline.length > 0 && (
        <>
          <div
            className={styles.sectionTitle}
            onClick={() => toggleSection("procedure")}
          >
            <span>Procedura akceptacji</span>
            <FontAwesomeIcon
              className={styles.sectionChevron}
              icon={collapsed.procedure ? faChevronRight : faChevronDown}
            />
          </div>
          {!collapsed.procedure && (
            <div className={styles.steps}>
              {timeline.map((ev, i) => {
                const isLast = i === timeline.length - 1;
                return (
                  <div key={`${ev.when}-${i}`} className={styles.step}>
                    <div className={styles.stepLine}>
                      <div
                        className={styles.stepDot}
                        style={{ background: ACTION_COLOR[ev.action] }}
                      />
                      {!isLast && <div className={styles.stepConnector} />}
                    </div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepNumber}>
                        {fmtDateTime(ev.when)}
                      </div>
                      <div className={styles.stepName}>
                        {ACTION_LABEL[ev.action]}
                        {ev.stepName ? ` — ${ev.stepName}` : ""}
                      </div>
                      {ev.who && (
                        <div className={styles.formulaBlock}>{ev.who}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvoiceMetricBody;
